import {
  ShipmentStatus,
  ShipmentType,
} from "@api/graphql/generated/generated-types";
import { StopOrderItem, isMultiDropOffType } from "./stopOrderRowRules";

/**
 * The order the dispatcher gave the stops, in form order: an existing stop's id,
 * or null for a stop that is created by the same save.
 */
export type StopOrderHint = Array<string | null>;

export interface StopOrderEditing {
  mode: StopOrderMode;
  baselineStopIds: string[];
  setStopOrderHint: (hint: StopOrderHint | null) => void;
}

/**
 * How a changed stop order is saved: hand-off (Confirm Route is the save), entry-with-confirm
 * (entry numbers, then Confirm Route), entry (entry numbers only), or hidden (no controls).
 */
export type StopOrderMode = "hand-off" | "entry-with-confirm" | "entry" | "hidden";

interface StopOrderModeInput {
  shouldKeepOrderEntrySequence: boolean;
  shipmentType: ShipmentType;
  shipmentStatus: ShipmentStatus;
  hasDriver: boolean;
  isRouteBased: boolean;
  isStoredOrderConsistent: boolean;
}

interface SavedStop {
  id: string;
  orderEntrySequenceNumber?: number | null;
}

const ACTIVE_STATUSES = new Set<ShipmentStatus>([
  ShipmentStatus.Assigned,
  ShipmentStatus.InTransit,
  ShipmentStatus.Routed,
]);

function getModeForOrderWithDriver(input: StopOrderModeInput): StopOrderMode {
  if (!ACTIVE_STATUSES.has(input.shipmentStatus)) return "hidden";
  if (!input.shouldKeepOrderEntrySequence) return "hand-off";
  return input.isStoredOrderConsistent ? "entry-with-confirm" : "hidden";
}

function getModeForOrderWithoutDriver(input: StopOrderModeInput): StopOrderMode {
  if (input.shipmentStatus !== ShipmentStatus.Created) return "hidden";
  // The form only shows a saved reorder of a route-based order at a keep-order
  // courier, so it is not offered anywhere else.
  if (input.isRouteBased && !input.shouldKeepOrderEntrySequence) return "hidden";
  return input.isStoredOrderConsistent ? "entry" : "hidden";
}

/**
 * Which way the Edit Order form saves a stop order change, or "hidden" for no controls. No route
 * data is ever written. A route-based order is offered with a driver at an optimizing courier
 * (Confirm Route saves) or at a keep-order courier (the form shows the saved entry order).
 */
export function getStopOrderMode(input: StopOrderModeInput): StopOrderMode {
  if (!isMultiDropOffType(input.shipmentType)) return "hidden";
  return input.hasDriver
    ? getModeForOrderWithDriver(input)
    : getModeForOrderWithoutDriver(input);
}

export function isEntryNumberMode(mode: StopOrderMode): boolean {
  return mode === "entry" || mode === "entry-with-confirm";
}

/** A stop that already has a route number means the order sits on a route. */
export function isRouteBasedOrder(
  stops: Array<{ stopSequenceNumber?: number | null }>,
): boolean {
  return stops.some((stop) => stop.stopSequenceNumber != null);
}

/**
 * Entry numbers are saved in the order the form shows, which only works when completed stops are
 * shown in their stored order; pending stops may sit anywhere around them.
 */
export function isDisplayedOrderConsistentWithStoredOrder(
  displayedStopIds: string[],
  storedStops: Array<{
    id: string;
    completedAt?: number | null;
    orderEntrySequenceNumber?: number | null;
  }>,
): boolean {
  const displayedIndexById = new Map(
    displayedStopIds.map((id, index) => [id, index]),
  );
  const numberedStops = storedStops.filter(
    (stop) =>
      stop.orderEntrySequenceNumber != null && displayedIndexById.has(stop.id),
  );
  const completedStops = numberedStops.filter((stop) => stop.completedAt != null);
  return completedStops.every((completedStop) =>
    completedStops.every((otherCompletedStop) => {
      if (otherCompletedStop.id === completedStop.id) return true;
      const storedDifference =
        (completedStop.orderEntrySequenceNumber as number) -
        (otherCompletedStop.orderEntrySequenceNumber as number);
      const displayedDifference =
        (displayedIndexById.get(completedStop.id) as number) -
        (displayedIndexById.get(otherCompletedStop.id) as number);
      return Math.sign(storedDifference) === Math.sign(displayedDifference);
    }),
  );
}

export function getCompletedStopIds(
  stops: Array<{ id: string; completedAt?: number | null }>,
): Set<string> {
  return new Set(
    stops.filter((stop) => stop.completedAt != null).map((stop) => stop.id),
  );
}

export function getPendingExistingStopIds(stops: StopOrderItem[]): string[] {
  return stops
    .filter((stop) => stop.id && !stop.isCompleted)
    .map((stop) => stop.id as string);
}

/**
 * True when the dispatcher reordered the pending stops, or put a new stop in
 * front of one. Adding or deleting stops at the end does not count.
 */
export function hasPendingStopOrderChange(
  baselineStopIds: string[],
  submittedStops: StopOrderItem[],
): boolean {
  const submittedIds = getPendingExistingStopIds(submittedStops);
  const submittedIdSet = new Set(submittedIds);
  const survivingBaselineIds = baselineStopIds.filter((id) =>
    submittedIdSet.has(id),
  );
  const isExistingOrderChanged =
    survivingBaselineIds.length !== submittedIds.length ||
    survivingBaselineIds.some((id, index) => id !== submittedIds[index]);
  const lastExistingPendingIndex = submittedStops.reduce(
    (lastIndex, stop, index) =>
      stop.id && !stop.isCompleted ? index : lastIndex,
    -1,
  );
  const hasNewStopBeforeExistingStop = submittedStops.some(
    (stop, index) => !stop.id && index < lastExistingPendingIndex,
  );
  return isExistingOrderChanged || hasNewStopBeforeExistingStop;
}

export function makeStopOrderHint(submittedStops: StopOrderItem[]): StopOrderHint {
  return submittedStops.map((stop) => stop.id ?? null);
}

/**
 * Turns the hint into stop ids once the save has created the new stops: the nth new stop in
 * entry order is the nth null in the hint.
 */
export function getDesiredStopIds(
  hint: StopOrderHint,
  savedStops: SavedStop[],
): { desiredStopIds: string[]; newStopIds: string[] } {
  const hintedIds = new Set(hint.filter((id): id is string => id !== null));
  const newStopIds = savedStops
    .filter((stop) => !hintedIds.has(stop.id))
    .sort(
      (a, b) =>
        (a.orderEntrySequenceNumber ?? Number.MAX_SAFE_INTEGER) -
        (b.orderEntrySequenceNumber ?? Number.MAX_SAFE_INTEGER),
    )
    .map((stop) => stop.id);
  let nextNewStopIndex = 0;
  const desiredStopIds: string[] = [];
  hint.forEach((id) => {
    if (id !== null) {
      desiredStopIds.push(id);
      return;
    }
    const newStopId = newStopIds[nextNewStopIndex];
    nextNewStopIndex += 1;
    if (newStopId) desiredStopIds.push(newStopId);
  });
  newStopIds.slice(nextNewStopIndex).forEach((id) => desiredStopIds.push(id));
  return { desiredStopIds, newStopIds };
}
